import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CSATReports from '../../api/csatReports';
import ReportsAPI from '../../api/reports';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { REPORTS_EVENTS } from '../../helper/AnalyticsHelper/events';

const computeDistribution = (value, total) =>
  ((value * 100) / total).toFixed(1);

export const state = {
  records: [],
  meta: {
    count: 0,
    currentPage: 1,
  },
  metrics: {
    totalResponseCount: 0,
    ratingsCount: {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
    },
    totalSentMessagesCount: 0,
    averageRating: null,
    satisfactionRate: null,
    negativeRate: null,
    responseRate: null,
    displayType: 'emoji',
  },
  comparisons: [],
  dynamics: [],
  breakdown: [],
  uiFlags: {
    isFetching: false,
    isFetchingMetrics: false,
    isFetchingDynamics: false,
    isFetchingBreakdown: false,
  },
};

export const getters = {
  getCSATResponses(_state) {
    return _state.records;
  },
  getMeta(_state) {
    return _state.meta;
  },
  getMetrics(_state) {
    return _state.metrics;
  },
  getComparisons(_state) {
    return _state.comparisons;
  },
  getDynamics(_state) {
    return _state.dynamics;
  },
  getBreakdown(_state) {
    return _state.breakdown;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getSatisfactionScore(_state) {
    if (!_state.metrics.totalSentMessagesCount) {
      return null;
    }
    if (!_state.metrics.totalResponseCount) {
      return null;
    }
    if (_state.metrics.satisfactionRate !== null) {
      return _state.metrics.satisfactionRate;
    }
    return computeDistribution(
      _state.metrics.ratingsCount[4] + _state.metrics.ratingsCount[5],
      _state.metrics.totalResponseCount
    );
  },
  getResponseRate(_state) {
    if (!_state.metrics.totalSentMessagesCount) {
      return null;
    }
    if (_state.metrics.responseRate !== null) {
      return _state.metrics.responseRate;
    }
    return computeDistribution(
      _state.metrics.totalResponseCount,
      _state.metrics.totalSentMessagesCount
    );
  },
  getAverageRating(_state) {
    if (
      !_state.metrics.totalSentMessagesCount ||
      !_state.metrics.totalResponseCount
    ) {
      return null;
    }
    return _state.metrics.averageRating;
  },
  getNegativeRate(_state) {
    if (!_state.metrics.totalSentMessagesCount) {
      return null;
    }
    return _state.metrics.negativeRate;
  },
  getRatingPercentage(_state) {
    if (!_state.metrics.totalResponseCount) {
      return { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    }
    return {
      1: computeDistribution(
        _state.metrics.ratingsCount[1],
        _state.metrics.totalResponseCount
      ),
      2: computeDistribution(
        _state.metrics.ratingsCount[2],
        _state.metrics.totalResponseCount
      ),
      3: computeDistribution(
        _state.metrics.ratingsCount[3],
        _state.metrics.totalResponseCount
      ),
      4: computeDistribution(
        _state.metrics.ratingsCount[4],
        _state.metrics.totalResponseCount
      ),
      5: computeDistribution(
        _state.metrics.ratingsCount[5],
        _state.metrics.totalResponseCount
      ),
    };
  },
  getRatingCount(_state) {
    return _state.metrics.ratingsCount;
  },
};

export const actions = {
  get: async function getResponses({ commit }, params) {
    commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetching: true });
    try {
      const response = await CSATReports.get(params);
      commit(types.SET_CSAT_RESPONSE, response.data.payload || response.data);
      commit(types.SET_CSAT_RESPONSE_META, response.data.meta || {});
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetching: false });
    }
  },
  getMetrics: async function getMetrics({ commit }, params) {
    commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetchingMetrics: true });
    try {
      const response = await CSATReports.getMetrics(params);
      commit(types.SET_CSAT_RESPONSE_METRICS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetchingMetrics: false });
    }
  },
  getDynamics: async function getDynamics({ commit }, params) {
    commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetchingDynamics: true });
    try {
      const response = await CSATReports.getDynamics(params);
      commit(types.SET_CSAT_DYNAMICS, response.data || []);
    } catch (error) {
      commit(types.SET_CSAT_DYNAMICS, []);
    } finally {
      commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetchingDynamics: false });
    }
  },
  getBreakdown: async function getBreakdown({ commit }, params) {
    commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetchingBreakdown: true });
    try {
      const response = await CSATReports.getBreakdown(params);
      commit(types.SET_CSAT_BREAKDOWN, response.data || []);
    } catch (error) {
      commit(types.SET_CSAT_BREAKDOWN, []);
    } finally {
      commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetchingBreakdown: false });
    }
  },
  downloadCSATReports(_, params) {
    return ReportsAPI.downloadSectionCsv('csat', params, params.fileName)
      .then(() => {
        AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
          reportType: 'csat',
        });
      })
      .catch(() => null);
  },
  update: async ({ commit }, { id, reviewNotes }) => {
    const response = await CSATReports.update(id, {
      csat_review_notes: reviewNotes,
    });
    commit(types.UPDATE_CSAT_RESPONSE, response.data);
    return response.data;
  },
};

export const mutations = {
  [types.SET_CSAT_RESPONSE_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_CSAT_RESPONSE]: MutationHelpers.set,
  [types.SET_CSAT_RESPONSE_META](_state, meta = {}) {
    _state.meta = {
      count: meta.count || 0,
      currentPage: meta.current_page || 1,
    };
  },
  [types.SET_CSAT_DYNAMICS](_state, data) {
    _state.dynamics = data;
  },
  [types.SET_CSAT_BREAKDOWN](_state, data) {
    _state.breakdown = data;
  },
  [types.SET_CSAT_RESPONSE_METRICS](
    _state,
    {
      total_count: totalResponseCount,
      ratings_count: ratingsCount,
      total_sent_messages_count: totalSentMessagesCount,
      average_rating: averageRating,
      satisfaction_rate: satisfactionRate,
      negative_rate: negativeRate,
      response_rate: responseRate,
      display_type: displayType,
      comparisons = [],
    }
  ) {
    _state.metrics.totalResponseCount = totalResponseCount || 0;
    _state.metrics.ratingsCount = {
      1: ratingsCount?.['1'] || 0,
      2: ratingsCount?.['2'] || 0,
      3: ratingsCount?.['3'] || 0,
      4: ratingsCount?.['4'] || 0,
      5: ratingsCount?.['5'] || 0,
    };
    _state.metrics.totalSentMessagesCount = totalSentMessagesCount || 0;
    _state.metrics.averageRating = averageRating ?? null;
    _state.metrics.satisfactionRate = satisfactionRate ?? null;
    _state.metrics.negativeRate = negativeRate ?? null;
    _state.metrics.responseRate = responseRate ?? null;
    _state.metrics.displayType = displayType || 'emoji';
    _state.comparisons = Array.isArray(comparisons) ? comparisons : [];
  },
  [types.UPDATE_CSAT_RESPONSE]: MutationHelpers.update,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
